/**
 * Sprint TF-A — HM Admin TalentFix Oversight workspace (read-only).
 *
 * Founder directive: minimum-viable oversight. Reuse existing
 * aggregators only. No charts, no analytics, no new workflows. Mirrors
 * the TalentTrain TT-C and TalentStart TS-B workspace patterns.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import { Loader2, FileText, ArrowRight, Wrench } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

function StatBox({ label, value, testid, accent = false }) {
  return (
    <Card className={`p-4 shadow-card ${accent ? 'border-primary/40' : ''}`}
          data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </p>
      <p className="text-2xl font-bold tabular-nums mt-1">{value}</p>
    </Card>
  );
}

export default function AdminTalentFixOversight() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminApi.tfAdminOversight();
      setData(d);
    } catch (e) {
      toast.error(t('toasts.loadFailed'), { description: formatApiError(e) });
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="py-10 text-center text-muted-foreground"
           data-testid="adm-tf-oversight-loading">
        <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
        {t('common.loading')}
      </div>
    );
  }

  const k = data.kpi || {};

  return (
    <div className="space-y-5 p-6 lg:p-8" data-testid="adm-tf-oversight">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wrench className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold">
            {t('adminTalentFix.oversight.title',
               { defaultValue: 'TalentFix Oversight' })}
          </h1>
        </div>
        <Link to="/admin/talentfix"
              className="text-xs text-muted-foreground hover:text-primary"
              data-testid="adm-tf-oversight-back">
          {t('adminTalentFix.oversight.back',
             { defaultValue: '← Back to Case board' })}
        </Link>
      </div>

      {/* KPI strip — 6 cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 2xl:grid-cols-6 gap-3">
        <StatBox label={t('adminTalentFix.oversight.kpi.pending',
                          { defaultValue: 'Pending acceptance' })}
                 value={k.pending_acceptance ?? 0}
                 testid="adm-tf-kpi-pending" accent />
        <StatBox label={t('adminTalentFix.oversight.kpi.awaitingConsent',
                          { defaultValue: 'Awaiting consent' })}
                 value={k.awaiting_consent ?? 0}
                 testid="adm-tf-kpi-awaiting-consent" />
        <StatBox label={t('adminTalentFix.oversight.kpi.investigation',
                          { defaultValue: 'Investigation' })}
                 value={k.investigation_active ?? 0}
                 testid="adm-tf-kpi-investigation" />
        <StatBox label={t('adminTalentFix.oversight.kpi.mediation',
                          { defaultValue: 'Mediation' })}
                 value={k.mediation_active ?? 0}
                 testid="adm-tf-kpi-mediation" />
        <StatBox label={t('adminTalentFix.oversight.kpi.closed',
                          { defaultValue: 'Closed' })}
                 value={k.closed ?? 0}
                 testid="adm-tf-kpi-closed" />
        <StatBox label={t('adminTalentFix.oversight.kpi.closurePending',
                          { defaultValue: 'Closure pack pending' })}
                 value={k.closure_pack_pending ?? 0}
                 testid="adm-tf-kpi-closure-pending" />
      </div>

      <Tabs defaultValue="awaiting" className="w-full">
        <TabsList data-testid="adm-tf-oversight-tabs">
          <TabsTrigger value="awaiting" data-testid="adm-tf-tab-awaiting">
            {t('adminTalentFix.oversight.tabs.awaiting',
               { defaultValue: 'Awaiting acceptance' })} ({(data.awaiting_acceptance || []).length})
          </TabsTrigger>
          <TabsTrigger value="stuck" data-testid="adm-tf-tab-stuck">
            {t('adminTalentFix.oversight.tabs.stuck',
               { defaultValue: 'Stuck in stage' })} ({(data.stuck_in_stage || []).length})
          </TabsTrigger>
          <TabsTrigger value="closure" data-testid="adm-tf-tab-closure">
            {t('adminTalentFix.oversight.tabs.closure',
               { defaultValue: 'Closure pending' })} ({(data.closure_pending || []).length})
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="adm-tf-tab-reports">
            {t('adminTalentFix.oversight.tabs.reports',
               { defaultValue: 'Recent reports' })} ({(data.reports_recent || []).length})
          </TabsTrigger>
        </TabsList>

        {/* Awaiting acceptance */}
        <TabsContent value="awaiting" className="mt-4">
          <Card className="p-5 shadow-card">
            {(data.awaiting_acceptance || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center"
                 data-testid="adm-tf-awaiting-empty">
                {t('adminTalentFix.oversight.awaiting.empty',
                   { defaultValue: 'Nothing pending — good news.' })}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.title', { defaultValue: 'Title' })}</TableHead>
                    <TableHead>{t('common.origin', { defaultValue: 'Origin' })}</TableHead>
                    <TableHead>{t('common.severity', { defaultValue: 'Severity' })}</TableHead>
                    <TableHead>{t('common.created', { defaultValue: 'Created' })}</TableHead>
                    <TableHead className="text-right">{t('common.open', { defaultValue: 'Open' })}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.awaiting_acceptance || []).map((c, i) => (
                    <TableRow key={c.id} data-testid={`adm-tf-awaiting-row-${i}`}>
                      <TableCell className="font-medium text-sm">{c.title || '—'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{c.origin_type || '—'}</Badge></TableCell>
                      <TableCell className="text-xs">{c.severity || '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{(c.created_at || '').slice(0, 10)}</TableCell>
                      <TableCell className="text-right">
                        <Link to={`/admin/talentfix?caseId=${c.id}`}
                              data-testid={`adm-tf-awaiting-open-${i}`}>
                          <Button size="sm" variant="ghost" className="h-7 text-xs">
                            <ArrowRight className="h-3 w-3 mr-1" />
                            {t('common.open', { defaultValue: 'Open' })}
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* Stuck in stage */}
        <TabsContent value="stuck" className="mt-4">
          <Card className="p-5 shadow-card">
            {(data.stuck_in_stage || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center"
                 data-testid="adm-tf-stuck-empty">
                {t('adminTalentFix.oversight.stuck.empty',
                   { defaultValue: 'No cases stuck in stage.' })}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.title', { defaultValue: 'Title' })}</TableHead>
                    <TableHead className="text-center">{t('common.stage', { defaultValue: 'Stage' })}</TableHead>
                    <TableHead>{t('common.updated', { defaultValue: 'Last update' })}</TableHead>
                    <TableHead className="text-right">{t('common.open', { defaultValue: 'Open' })}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.stuck_in_stage || []).map((c, i) => (
                    <TableRow key={c.id} data-testid={`adm-tf-stuck-row-${i}`}>
                      <TableCell className="font-medium text-sm">{c.title || '—'}</TableCell>
                      <TableCell className="text-center text-xs">{c.current_stage ?? '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{(c.updated_at || '').slice(0, 10)}</TableCell>
                      <TableCell className="text-right">
                        <Link to={`/admin/talentfix/cases/${c.id}/investigation`}
                              data-testid={`adm-tf-stuck-open-${i}`}>
                          <Button size="sm" variant="ghost" className="h-7 text-xs">
                            <ArrowRight className="h-3 w-3 mr-1" />
                            {t('common.open', { defaultValue: 'Open' })}
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* Closure pending */}
        <TabsContent value="closure" className="mt-4">
          <Card className="p-5 shadow-card">
            {(data.closure_pending || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center"
                 data-testid="adm-tf-closure-empty">
                {t('adminTalentFix.oversight.closure.empty',
                   { defaultValue: 'All closed cases have a closure pack.' })}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.title', { defaultValue: 'Title' })}</TableHead>
                    <TableHead>{t('common.closedAt', { defaultValue: 'Closed at' })}</TableHead>
                    <TableHead className="text-right">{t('common.render', { defaultValue: 'Render closure' })}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.closure_pending || []).map((c, i) => (
                    <TableRow key={c.id} data-testid={`adm-tf-closure-row-${i}`}>
                      <TableCell className="font-medium text-sm">{c.title || '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{(c.closed_at || '').slice(0, 10)}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => adminApi.tfRenderClosure(c.id, 'pdf')}
                                data-testid={`adm-tf-closure-pdf-${i}`}>PDF</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => adminApi.tfRenderClosure(c.id, 'docx')}
                                data-testid={`adm-tf-closure-docx-${i}`}>DOCX</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => adminApi.tfRenderClosure(c.id, 'xlsx')}
                                data-testid={`adm-tf-closure-xlsx-${i}`}>XLSX</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* Recent reports */}
        <TabsContent value="reports" className="mt-4">
          <Card className="p-5 shadow-card">
            {(data.reports_recent || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center"
                 data-testid="adm-tf-reports-empty">
                {t('adminTalentFix.oversight.reports.empty',
                   { defaultValue: 'No TalentFix reports yet.' })}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.kind', { defaultValue: 'Kind' })}</TableHead>
                    <TableHead>{t('common.format', { defaultValue: 'Format' })}</TableHead>
                    <TableHead>{t('common.filename', { defaultValue: 'File' })}</TableHead>
                    <TableHead>{t('common.released', { defaultValue: 'Released' })}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.reports_recent || []).map((r, i) => (
                    <TableRow key={r.id} data-testid={`adm-tf-report-row-${i}`}>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5">
                          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                          {r.kind || '—'}
                        </span>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs uppercase">{r.format || '—'}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.filename || '—'}</TableCell>
                      <TableCell className="text-sm">
                        {(r.released_at || '').slice(0, 19).replace('T', ' ') || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
